// src/app/services/report-state.service.ts

import { Injectable, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { ReportPreferenceService } from './report-preference.service';
import { ReportStateV1 } from '../shared/utils/report-state.types';

interface _WriteJob {
  userId: string;
  module: string;
  slug: string;
  state: ReportStateV1;
}

/** Milliseconds to debounce rapid filter/sort/resize events before writing. */
const WRITE_DEBOUNCE_MS = 500;

/**
 * ReportStateService
 * â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
 * High-level facade for reading and writing per-report UI state.
 *
 * Delegates all storage I/O to `ReportPreferenceService` (which owns the
 * `IPreferenceStorage` adapter).  This service adds:
 *   - debounced writes so rapid interactions don't thrash storage
 *   - one-shot "pending favourite config" handoff between landing pages and
 *     report components
 *   - convenience wrappers that resolve the userId automatically
 *
 * Storage key: `reportState:{userId}:{module}:{reportSlug}`
 *
 * Logout behaviour: Report states are NOT cleared on logout.  They survive
 * re-authentication so the same user's preferences are restored automatically.
 * Each user's data is isolated by their userId â€” no cross-user leakage.
 */
@Injectable({ providedIn: 'root' })
export class ReportStateService implements OnDestroy {
  private readonly _write$ = new Subject<_WriteJob>();
  private readonly _destroy$ = new Subject<void>();

  /**
   * One-shot pending favourite config ID.
   * A landing page sets this just before router.navigate(); the report's
   * ngOnInit consumes and clears it.
   */
  private _pendingFavConfigId: string | null = null;

  constructor(private prefSvc: ReportPreferenceService) {
    this._write$
      .pipe(debounceTime(WRITE_DEBOUNCE_MS), takeUntil(this._destroy$))
      .subscribe(({ userId, module, slug, state }) =>
        this.prefSvc.saveReportState(userId, module, slug, state)
      );
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  // â”€â”€ User context â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  /**
   * Return the current user's ID.
   * Exposed so ColumnPickerComponent can scope its storage key to the user.
   */
  getCurrentUserId(): string | null {
    return this.prefSvc.getCurrentUserId();
  }

  // â”€â”€ Core state CRUD â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  /**
   * Load the persisted state for the current user's report.
   * Returns null when nothing is saved, the data is corrupt, or the schema
   * version does not match.
   */
  load(slug: string, module: string): ReportStateV1 | null {
    const userId = this.prefSvc.getCurrentUserId();
    if (!userId || !slug || !module) return null;
    return this.prefSvc.getReportState(userId, module, slug);
  }

  /**
   * Merge a partial state patch and schedule a debounced write.
   * Safe to call on every user interaction â€” writes are collapsed.
   *
   * Existing fields not present in `patch` are preserved (merge semantics).
   */
  save(
    slug: string,
    module: string,
    patch: Partial<Omit<ReportStateV1, '_v' | '_ts'>>
  ): void {
    const userId = this.prefSvc.getCurrentUserId();
    if (!userId || !slug || !module) return;
    const existing = this.load(slug, module) ?? { _v: 1 as const, _ts: 0 };
    const next: ReportStateV1 = {
      ...existing,
      ...patch,
      _v: 1,
      _ts: Date.now(),
    };
    this._write$.next({ userId, module, slug, state: next });
  }

  /**
   * Immediately remove the saved state for a report.
   * Call when the user explicitly resets / clears all filters.
   */
  clear(slug: string, module: string): void {
    const userId = this.prefSvc.getCurrentUserId();
    if (!userId || !slug || !module) return;
    this.prefSvc.clearReportState(userId, module, slug);
  }

  // â”€â”€ Pending favourite config handoff â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  /**
   * Signal that the next report open should restore a specific favourite config.
   * Call from a landing page just before router.navigate().
   */
  setPendingFavouriteConfig(configId: string): void {
    this._pendingFavConfigId = configId;
  }

  /**
   * Read and clear the pending config ID in one step.
   * Returns null when no pending config was set.
   * Report components call this once in ngOnInit.
   */
  consumePendingFavouriteConfigId(): string | null {
    const id = this._pendingFavConfigId;
    this._pendingFavConfigId = null;
    return id;
  }

  // â”€â”€ ID generator â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  /** Generate a collision-resistant unique ID. */
  static generateId(): string {
    if (typeof crypto?.randomUUID === 'function') return crypto.randomUUID();
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

